import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { database } from "../../database/firebase";
import { setBackButtonUrl } from '../uiSlice';

import "./lobby.css";
// import { syncJoinLobbyFromUrlSearch } from "../../utils/joinLobbyFromUrl";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";
const TOTAL_USER_COUNT_KEY = "mythFactTotalUserCount";

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

/** PIN digits must match the Firestore lobby document id (same normalization as host). */
function normalizeJoinPin(raw) {
  const t = String(raw ?? "").trim();
  if (!/^\d{1,4}$/.test(t)) return "";
  return t.padStart(4, "0");
}

export default function Lobby() {
  const navigate = useNavigate();
  const { data: themeData } = useSelector((state) => state.theme);
  const themeColors = themeData?.colors || themeData || {};
  const optionBgColor =
    typeof themeColors.option_color === "string" && themeColors.option_color.trim()
      ? themeColors.option_color
      : "#2a4a7a";

  console.log("optionBgColor", themeColors.option_color);
  const optionTextColor =
    typeof themeColors.option_text_color === "string" &&
    themeColors.option_text_color.trim()
      ? themeColors.option_text_color
      : "#ffffff";
  const titleTextStyle = themeColors?.text_color
    ? { color: themeColors.text_color }
    : undefined;
  const actionButtonStyle = {
    backgroundColor: optionBgColor,
    color: optionTextColor,
  };
  const storedUserData = useMemo(() => readStoredUserData(), []);
  const sessionId = storedUserData.sessionId;
  const storedLobbyName = String(storedUserData.lobby || "").trim();
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [joining, setJoining] = useState(false);
  const [roomFull, setRoomFull] = useState(false);
  const [showInitialLoader, setShowInitialLoader] = useState(true);
  const { status, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  
  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [status, user]);

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setShowInitialLoader(false);
    }, 2200);
    return () => window.clearTimeout(timerId);
  }, []);

  // useEffect(() => {
  //   const sessionId = String(user?.sessionId ?? "").trim().toLowerCase();
  //   if (!sessionId.startsWith("demo")) return;
  //   Swal.fire({
  //     icon: "warning",
  //     title: "Not a Solo Mission 😉",
  //     text: "This game is built for multiplayer action. Subscribe to join the fun with others.",
  //     confirmButtonText: "OK",
  //   }).then((result) => {
  //     if (result.isConfirmed) {
  //       window.location.assign("https://staging.extramileplay.com/plans");
  //     }
  //   });
  // }, [user?.sessionId]);


  const handleCreate = async () => {
    const totalUserCount = 2;
    try {
      sessionStorage.removeItem(CURRENT_LOBBY_KEY);
      sessionStorage.setItem(TOTAL_USER_COUNT_KEY, String(totalUserCount));
    } catch {
      // ignore storage write failures
    }
    navigate("/host", {
      state: { playerCount: totalUserCount, totalUserCount },
    });
  };

  const handleNext = async () => {
    if (!pin.trim()) {
      setPinError("Please enter a PIN");
      return;
    }
    const userId =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    if (!sessionId || !userId) {
      setPinError("Missing session details. Please login again.");
      return;
    }

    const lobbyNameFromPin = normalizeJoinPin(pin.trim());
    if (!lobbyNameFromPin) {
      setPinError("Enter a valid 4-digit PIN");
      return;
    }

    setPinError("");
    setJoining(true);

    try {
      const lobbyDocRef = doc(
        database,
        `lobbies/${sessionId}/lobby/${lobbyNameFromPin}`
      );
      const lobbySnap = await getDoc(lobbyDocRef);
      if (!lobbySnap.exists()) {
        await Swal.fire({
          icon: "error",
          text: "Wrong lobby pin",
          confirmButtonText: "OK",
        });
        return;
      }
      const lobbyStatus = String(lobbySnap.data()?.status ?? "").trim().toLowerCase();
      if (lobbyStatus === "gameover") {
        navigate("/leaderboard");
        return;
      }

      const rawCap = Number(lobbySnap.data()?.totalUserCount);
      const maxPlayers = rawCap === 2 ? 2 : 4;

      const usersRef = collection(
        database,
        `lobbies/${sessionId}/lobby/${lobbyNameFromPin}/users`
      );
      const usersSnap = await getDocs(usersRef);
      const currentUserDocRef = doc(
        database,
        `lobbies/${sessionId}/lobby/${lobbyNameFromPin}/users/${userId}`
      );
      const currentUserDocSnap = await getDoc(currentUserDocRef);
      const isAlreadyInLobby = currentUserDocSnap.exists();
      const isRoomFull =
        usersSnap.size >= maxPlayers && !isAlreadyInLobby;

      if (isRoomFull) {
        await Swal.fire({
          icon: "warning",
          text: "Room is full. You can't join this lobby. Create your own lobby.",
          confirmButtonText: "OK",
        });
        return;
      }

      try {
        sessionStorage.setItem(CURRENT_LOBBY_KEY, lobbyNameFromPin);
        sessionStorage.setItem(TOTAL_USER_COUNT_KEY, String(maxPlayers));
      } catch {
        // ignore storage failures
      }
      try {
        const merged = { ...storedUserData, lobby: lobbyNameFromPin };
        const payload = JSON.stringify(merged);
        localStorage.setItem("userData", payload);
        sessionStorage.setItem("userData", payload);
      } catch {
        // ignore storage failures
      }

      const pinNum = Number.parseInt(lobbyNameFromPin, 10);
      navigate("/waiting", {
        state: {
          pin: pinNum,
          loginPin: pinNum,
          lobbyName: lobbyNameFromPin,
        },
      });
    } catch (firestoreError) {
      console.error("Lobby join failed:", firestoreError);
      await Swal.fire({
        icon: "error",
        text: "Could not join lobby. Try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setJoining(false);
    }
  };

  useEffect(() => {
    document.documentElement.classList.add("lobby-static-bg");
    document.body.classList.add("lobby-static-bg");
    return () => {
      document.documentElement.classList.remove("lobby-static-bg");
      document.body.classList.remove("lobby-static-bg");
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const checkLobbyCapacity = async () => {
      if (!sessionId || !storedLobbyName) {
        if (isMounted) setRoomFull(false);
        return;
      }
      try {
        const lobbyDocRef = doc(
          database,
          `lobbies/${sessionId}/lobby/${storedLobbyName}`
        );
        const lobbySnap = await getDoc(lobbyDocRef);
        if (!lobbySnap.exists()) {
          if (isMounted) setRoomFull(false);
          return;
        }
        const lobbyStatus = String(lobbySnap.data()?.status ?? "").trim().toLowerCase();
        if (lobbyStatus === "gameover") {
          navigate("/leaderboard");
          return;
        }

        const rawCap = Number(lobbySnap.data()?.totalUserCount);
        const maxPlayers = rawCap === 2 ? 2 : 4;

        const currentUserId =
          storedUserData.userId || storedUserData.userid || storedUserData.id;
        const usersRef = collection(
          database,
          `lobbies/${sessionId}/lobby/${storedLobbyName}/users`
        );
        const usersSnap = await getDocs(usersRef);
        const isRoomFullByCount = usersSnap.size >= maxPlayers;

        const currentUserDocRef = currentUserId
          ? doc(
              database,
              `lobbies/${sessionId}/lobby/${storedLobbyName}/users/${currentUserId}`
            )
          : null;
        const currentUserDocSnap = currentUserDocRef
          ? await getDoc(currentUserDocRef)
          : null;
        const isSameUserAlreadyJoined = currentUserDocSnap
          ? currentUserDocSnap.exists()
          : false;

        const shouldBlockForFullRoom =
          isRoomFullByCount && !isSameUserAlreadyJoined;

        if (!isMounted) return;
        setRoomFull(shouldBlockForFullRoom);
        if (shouldBlockForFullRoom) {
          await Swal.fire({
            icon: "warning",
            text: "Room is full. You can't join this lobby. Create your own lobby.",
            confirmButtonText: "OK",
          });
          return;
        }

        const pinNum = Number.parseInt(String(storedLobbyName).trim(), 10);
        navigate("/waiting", {
          state: {
            pin: pinNum,
            loginPin: pinNum,
            lobbyName: storedLobbyName,
          },
        });
      } catch (firestoreError) {
        console.error("Failed to check lobby capacity:", firestoreError);
      }
    };

    checkLobbyCapacity();

    return () => {
      isMounted = false;
    };
  }, [sessionId, storedLobbyName, navigate]);

  if (showInitialLoader) {
    return (
      <div className="lobby-page lobby-page--loading">
        <div className="lobby-initial-loader" />
        <p className="lobby-loading-text" style={titleTextStyle}>Loading lobby...</p>
      </div>
    );
  }

  return (
    <div className="lobby-page">
      <h1
        className="lobby-title"
        style={titleTextStyle}
      >
        LOBBY
      </h1>

      <div className="lobby-cards">
        <section className="lobby-glassCard" aria-label="Create lobby">
          <h2 className="lobby-cardTitle" style={titleTextStyle}>
            CREATE
          </h2>
          <div className="lobby-cardBody">
            <button
              type="button"
              className="lobby-createBtn"
              onClick={handleCreate}
              style={actionButtonStyle}
            >
              Create
            </button>
          </div>
        </section>

        <section className="lobby-glassCard" aria-label="Join lobby">
          <h2 className="lobby-cardTitle" style={titleTextStyle}>
            JOIN
          </h2>
          <div className="lobby-cardBody lobby-cardBody--join">
            <div className="lobby-field lobby-field--flush">
              <input
                className="lobby-pinInput"
                type="text"
                placeholder="Enter PIN"
                value={pin}
                maxLength={4}
                onChange={(e) => {
                  setPin(e.target.value);
                  setPinError("");
                }}
              />
              {pinError && <p className="lobby-errorText">{pinError}</p>}
            </div>
          </div>
        </section>
      </div>

      <button
        type="button"
        className="lobby-nextBtn"
        onClick={handleNext}
        disabled={joining || roomFull}
        style={actionButtonStyle}
      >
        {joining ? "Joining..." : "Next"}
      </button>
    </div>
  );
}
